<template>
    <div class="app-shell"
         :class="{
             'sidebar-collapsed': sidebarCollapsed,
             'mobile-nav-open': mobileNavOpen,
             'top-bar-scrolled': isScrolled
         }">
        <header class="top-bar">
            <div class="top-bar__start">
                <button type="button" class="top-bar__icon-button top-bar__burger"
                        aria-label="Menü umschalten" @click="toggleSidebar">
                    <i class="material-icons">menu</i>
                </button>
                <router-link class="brand" :to="{name: 'dashboard'}">
                    <span class="brand__logo">
                        <img id="top-small-logo"
                             src="https://cdn.demons-gaming.cc/gadget/dg_logo_white_small.png"
                             alt="DiamondLifeRP Logo">
                    </span>
                    <span class="brand__text">
                        <span class="brand__title">DiamondLifeRP</span>
                        <span class="brand__subtitle">Gadget</span>
                    </span>
                </router-link>
            </div>

            <div id="header-toolbar" class="top-bar__actions" role="toolbar">
                <template v-if="loggedIn">
                    <button id="account-icon" type="button"
                            class="top-bar__icon-button top-bar__points"
                            title="Loyalty Points" @click="goToUser">
                        <i class="material-icons">account_balance_wallet</i>
                        <span class="top-bar__points-value">{{ loyaltyPoints }}</span>
                    </button>
                    <router-link :to="{name: 'premium'}"
                                 class="top-bar__icon-button top-bar__premium"
                                 title="Premium">
                        <i class="material-icons">workspace_premium</i>
                    </router-link>
                    <router-link :to="{name: 'user-notification'}"
                                 class="top-bar__icon-button"
                                 title="Benachrichtigungen">
                        <i class="material-icons">notifications</i>
                        <span class="top-bar__dot"></span>
                    </router-link>

                    <div class="user-menu" :class="{'is-open': userMenuOpen}">
                        <button type="button" class="user-menu__trigger" @click.stop="toggleUserMenu">
                            <img class="layout-avatar" :src="avatarSrc" alt="Avatar">
                            <span class="user-menu__name">{{ username }}</span>
                            <i class="material-icons user-menu__caret">expand_more</i>
                        </button>
                        <transition name="dropdown">
                            <div v-if="userMenuOpen" class="user-menu__dropdown" @click.stop>
                                <div class="user-menu__header">
                                    <img class="layout-avatar layout-avatar--large" :src="avatarSrc"
                                         alt="Avatar">
                                    <div class="user-menu__header-text">
                                        <strong>{{ username }}</strong>
                                        <span>{{ loyaltyPoints }} Loyalty Points</span>
                                    </div>
                                </div>
                                <router-link :to="{name: 'user'}" class="user-menu__item" @click="onNavClick">
                                    <i class="material-icons">person</i>
                                    Profil
                                </router-link>
                                <router-link :to="{name: 'user-notification'}" class="user-menu__item"
                                             @click="onNavClick">
                                    <i class="material-icons">notifications</i>
                                    Benachrichtigungen
                                </router-link>
                                <router-link :to="{name: 'premium'}" class="user-menu__item"
                                             @click="onNavClick">
                                    <i class="material-icons">workspace_premium</i>
                                    Premium
                                </router-link>
                                <router-link :to="{name: 'logout'}"
                                             class="user-menu__item user-menu__item--danger"
                                             @click="onNavClick">
                                    <i class="material-icons">logout</i>
                                    Abmelden
                                </router-link>
                            </div>
                        </transition>
                    </div>
                </template>
                <router-link v-else :to="{name: 'login'}" class="top-bar__login">
                    <i class="material-icons">login</i>
                    Anmelden
                </router-link>
            </div>
        </header>

        <aside class="sidebar">
            <div class="sidebar__search">
                <i class="material-icons">search</i>
                <input v-model="navFilter" type="text" placeholder="Menü durchsuchen..."
                       aria-label="Menü durchsuchen">
                <button v-if="navFilter" type="button" class="sidebar__search-clear"
                        aria-label="Suche leeren" @click="navFilter = ''">
                    <i class="material-icons">close</i>
                </button>
            </div>

            <nav class="sidebar__nav" @scroll="hideNavTooltip">
                <div v-for="section in visibleNavSections" :key="`section_${section.title}`"
                     class="sidebar__section">
                    <div class="sidebar__section-title">{{ section.title }}</div>
                    <component v-for="(item, index) in section.items"
                               :key="item.name"
                               :is="item.href ? 'a' : 'router-link'"
                               v-bind="navLinkBindings(item)"
                               :id="item.id"
                               class="sidebar__item"
                               :class="{'is-active': isActive(item)}"
                               :style="{'--item-index': index}"
                               @click="onNavClick"
                               @mouseenter="showNavTooltip(item, $event)"
                               @mouseleave="hideNavTooltip">
                        <span class="sidebar__item-indicator"></span>
                        <i class="material-icons sidebar__item-icon">{{ item.icon }}</i>
                        <span class="sidebar__item-label">{{ item.label }}</span>
                        <i v-if="item.href" class="material-icons sidebar__item-external">open_in_new</i>
                    </component>
                </div>
                <div v-if="!visibleNavSections.length" class="sidebar__empty">
                    <i class="material-icons">search_off</i>
                    Keine Einträge gefunden
                </div>
            </nav>

            <div class="sidebar__footer">
                <button type="button" class="sidebar__collapse" @click="toggleSidebar">
                    <i class="material-icons">chevron_left</i>
                    <span>Einklappen</span>
                </button>
            </div>
        </aside>

        <transition name="fade">
            <div v-if="mobileNavOpen" class="sidebar-backdrop" @click="closeMobileNav"></div>
        </transition>

        <div v-if="navTooltip.visible" class="nav-tooltip"
             :style="{top: navTooltip.top + 'px', left: navTooltip.left + 'px'}">
            {{ navTooltip.label }}
        </div>

        <main id="page-content" class="page-content mdc">
            <div id="snackbar" class="mdl-js-snackbar mdl-snackbar">
                <div class="mdl-snackbar__action"></div>
                <div class="mdl-snackbar__text"></div>
            </div>
            <div id="popup-wrapper"></div>
            <!-- flash messages -->
            <div v-if="flashMessages.length" class="status-messages">
                <div v-for="entry in flashMessages" :class="entry.class">
                    <i class="material-icons md-light">{{entry.icon}}</i>
                    <div v-html="entry.text"></div>
                </div>
            </div>
            <slot name="page-content"></slot>
            <div id="footer" class="row">
                <div class="col-lg-12">
                    <hr class="footer__divider">
                    <div class="footer__inner">
                        <ul class="footer-link-list">
                            <li><a href="https://diamondliferp.cc/" target="_blank">©{{ new
                            Date().getFullYear() }}
                                DiamondLifeRP</a></li>
                        </ul>
                        <ul class="footer-link-list footer-link-list--meta">
                            <li><a href="https://diamondliferp.cc/index.php?datenschutzerklaerung/">Datenschutzerklärung</a>
                            </li>
                            <li><a href="https://diamondliferp.cc/index.php?legal-notice/">Impressum</a></li>
                        </ul>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>
<script lang="ts">
import {Component, Vue} from 'vue-property-decorator'
import {State} from "vuex-class";
import {Location} from "vue-router";

interface NavItem {
    name: string;
    label: string;
    icon: string;
    route?: Location;
    href?: string;
    auth?: boolean;
    id?: string;
}

interface NavSection {
    title: string;
    items: NavItem[];
}

const MOBILE_BREAKPOINT = 900;
const SIDEBAR_STORAGE_KEY = 'gadget:sidebar-collapsed';

@Component({
    name: 'Layout',
})
export default class Layout extends Vue {
    @State('loggedIn') loggedIn: any;

    @State('flashMessages') flashMessages: FlashMessageData[];

    @State('layoutData') layoutData: LayoutData;

    sidebarCollapsed: boolean = false;

    mobileNavOpen: boolean = false;

    userMenuOpen: boolean = false;

    navFilter: string = '';

    isScrolled: boolean = false;

    navTooltip: {visible: boolean, label: string, top: number, left: number} = {
        visible: false,
        label: '',
        top: 0,
        left: 0,
    };

    navSections: NavSection[] = [
        {
            title: 'Übersicht',
            items: [
                {name: 'dashboard', label: 'Dashboard', icon: 'dashboard', route: {name: 'dashboard'}, id: 'dashboard-link'},
                {name: 'servers', label: 'Server', icon: 'dns', route: {name: 'servers'}},
                {name: 'leaderboard', label: 'Leaderboard', icon: 'leaderboard', route: {name: 'leaderboard'}},
                {name: 'livemap', label: 'Livemap', icon: 'location_on', route: {name: 'livemap'}, auth: true},
            ],
        },
        {
            title: 'Spieler',
            items: [
                {name: 'accounts', label: 'Accounts', icon: 'person', route: {name: 'accounts'}, auth: true, id: 'players-link'},
                {name: 'vehicles', label: 'Fahrzeuge', icon: 'directions_car', href: '/vehicles', auth: true, id: 'vehicles-link'},
                {name: 'houses', label: 'Immobilien', icon: 'home', href: '/houses', auth: true, id: 'houses-link'},
                {name: 'gangs', label: 'Fraktionen', icon: 'groups', href: '/gangs', auth: true, id: 'gangs-link'},
                {name: 'shop', label: 'Punkte Shop', icon: 'shopping_cart', route: {name: 'shop'}, auth: true},
            ],
        },
        {
            title: 'Community',
            items: [
                {name: 'whitelist', label: 'Whitelist', icon: 'verified_user', route: {name: 'whitelist'}},
                {name: 'issue-tracker', label: 'Issue Tracker', icon: 'bug_report', route: {name: 'issue-tracker'}},
                {name: 'support', label: 'Support', icon: 'contact_support', route: {name: 'support'}, id: 'supportcases-link'},
            ],
        },
        {
            title: 'Verwaltung',
            items: [
                {name: 'analytics', label: 'Analytics', icon: 'insights', route: {name: 'analytics'}, auth: true},
                {name: 'rcon', label: 'Rcon', icon: 'settings_remote', route: {name: 'rcon'}, auth: true},
                {name: 'acp', label: 'Verwaltung', icon: 'admin_panel_settings', href: '/acp/home', auth: true, id: 'acp-link'},
            ],
        },
    ];

    get visibleNavSections(): NavSection[]
    {
        const term = this.navFilter.trim().toLowerCase();

        return this.navSections
            .map((section) => ({
                title: section.title,
                items: section.items.filter((item) =>
                    (!item.auth || this.loggedIn)
                    && (!term
                        || item.label.toLowerCase().includes(term)
                        || section.title.toLowerCase().includes(term)),
                ),
            }))
            .filter((section) => section.items.length > 0);
    }

    get username(): string
    {
        return this.currentUser ? this.currentUser.username : '';
    }

    get loyaltyPoints(): number
    {
        return this.currentUser ? (this.currentUser.loyalty_points || 0) : 0;
    }

    get avatarSrc(): string
    {
        return `https://${this.hostname}/application/index/avatar/uri/${this.layoutAvatarUrl}`;
    }

    get layoutAvatarUrl()
    {
        if (this.layoutData.avatar)
        {
            return window.btoa(this.layoutData.avatar);
        }
        else if (this.currentUser)
        {
            return window.btoa(`https://secure.gravatar.com/avatar/${this.currentUser.username}?s=320&amp;d=identicon`);
        }

        return '';
    }

    get hostname()
    {
        let hostname = window.location.hostname;

        if (hostname === 'localhost')
        {
            hostname = 'gadget.demons-gaming.cc';
        }

        return hostname;
    }

    mounted()
    {
        this.sidebarCollapsed = window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
        window.addEventListener('scroll', this.onScroll, {passive: true});
        window.addEventListener('resize', this.onResize);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        this.onScroll();
    }

    beforeDestroy()
    {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
    }

    navLinkBindings(item: NavItem): Record<string, any>
    {
        return item.href ? {href: item.href} : {to: item.route};
    }

    isActive(item: NavItem): boolean
    {
        if (!item.route)
        {
            return false;
        }

        const current = this.$route.path;

        if (item.route.name === 'dashboard')
        {
            return current === '/' || current === '/dashboard';
        }

        try
        {
            return current === this.$router.resolve(item.route).route.path;
        }
        catch (e)
        {
            return false;
        }
    }

    toggleSidebar()
    {
        if (window.innerWidth <= MOBILE_BREAKPOINT)
        {
            this.mobileNavOpen = !this.mobileNavOpen;
            return;
        }

        this.sidebarCollapsed = !this.sidebarCollapsed;
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, this.sidebarCollapsed ? '1' : '0');
    }

    closeMobileNav()
    {
        this.mobileNavOpen = false;
    }

    toggleUserMenu()
    {
        this.userMenuOpen = !this.userMenuOpen;
    }

    onNavClick()
    {
        this.closeMobileNav();
        this.userMenuOpen = false;
    }

    goToUser()
    {
        this.$router.push({name: 'user'}).catch(() => undefined);
    }

    onDocumentClick()
    {
        this.userMenuOpen = false;
    }

    onKeydown(event: KeyboardEvent)
    {
        if (event.key === 'Escape')
        {
            this.userMenuOpen = false;
            this.mobileNavOpen = false;
        }
    }

    onScroll()
    {
        this.isScrolled = window.scrollY > 4;
    }

    onResize()
    {
        if (window.innerWidth > MOBILE_BREAKPOINT && this.mobileNavOpen)
        {
            this.mobileNavOpen = false;
        }

        this.hideNavTooltip();
    }

    showNavTooltip(item: NavItem, event: MouseEvent)
    {
        if (!this.sidebarCollapsed || window.innerWidth <= MOBILE_BREAKPOINT)
        {
            return;
        }

        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();

        this.navTooltip = {
            visible: true,
            label: item.label,
            top: rect.top + (rect.height / 2),
            left: rect.right + 12,
        };
    }

    hideNavTooltip()
    {
        this.navTooltip.visible = false;
    }
}
</script>
